import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Component to guard content based on user permissions
 * @param {Object} props
 * @param {string} props.section - The admin section to check (products, categories, etc.)
 * @param {string} props.action - The action to check (create, read, update, delete)
 * @param {boolean} props.redirect - Whether to redirect to dashboard if no permission
 * @param {React.ReactNode} props.children - Content to render if permitted
 * @param {React.ReactNode} props.fallback - Content to render if not permitted (optional)
 */
const PermissionGuard = ({ 
  section, 
  action = 'read', 
  redirect = false, 
  children, 
  fallback = null 
}) => {
  const { hasPermission, canAccessSection, isSuperAdmin, role } = usePermissions();
  
  // Super admin and admin have full access
  if (isSuperAdmin || role === 'admin') {
    return <>{children}</>;
  }
  
  // Check if user has the required permission
  let hasAccess = false;
  
  if (action === 'read') {
    hasAccess = canAccessSection(section);
  } else {
    hasAccess = hasPermission(section, action);
  }
  
  if (hasAccess) {
    return <>{children}</>;
  }
  
  // Redirect to dashboard if no permission and redirect is enabled
  if (redirect) {
    return <Navigate to="/admin" replace />;
  }
  
  // Return fallback content or default access denied message
  if (fallback) {
    return <>{fallback}</>;
  }
  
  return (
    <div className="text-center py-5">
      <h2 className="text-danger">Access Denied</h2>
      <p className="text-muted">You don't have permission to access this content.</p>
    </div>
  );
};

export default PermissionGuard;
